import {
  MemoryDTO,
  MemoryLinkValue,
  MemoryType,
  MemoryValue,
  StepCheck,
  StepCheckOperatorType,
} from 'components/pages/Book/MemoryBank/memoryBankTypes';
import { getMemorySlotVariableLink } from 'components/utils/choiceMemoryUtils';

export type CheckStepValidationResult = {
  valid: boolean;
  error: string;
};

const OPERATORS_WITHOUT_SCALAR_VALUE = new Set([
  StepCheckOperatorType.Min,
  StepCheckOperatorType.Max,
  StepCheckOperatorType.ConditionList,
  StepCheckOperatorType.PlayerHas,
]);

/**
 * Validates that a check step's value is compatible with the variable type.
 * Extracted from AddBranch.formSlotValidate for testability.
 */
export function validateCheckStepValue(
  check: StepCheck,
  memoryBank: MemoryDTO[],
): CheckStepValidationResult {
  const ok: CheckStepValidationResult = { valid: true, error: '' };

  if (!check || !check.operator) {
    return { valid: false, error: 'Check operator is empty' };
  }

  // Min/Max/ConditionList/PlayerHas don't use scalar values — skip
  if (OPERATORS_WITHOUT_SCALAR_VALUE.has(check.operator)) {
    return ok;
  }

  if (!check.variableId) {
    return { valid: false, error: 'Check variable is empty' };
  }

  const variable = memoryBank.find((slot) => slot.id === Number(check.variableId));
  if (!variable) {
    return { valid: false, error: `Memory with id ${check.variableId} not found` };
  }

  const { value } = check;

  // Skip array values (number[] used by playerHas) and undefined
  if (Array.isArray(value) || value === undefined) {
    return ok;
  }

  // Variable-to-variable comparison — validate type match
  const memoryLink = getMemorySlotVariableLink(value as MemoryValue | MemoryLinkValue);
  if (memoryLink) {
    if (memoryLink.type === 'variable') {
      const comparedVariable = memoryBank.find((m) => m.id === Number(memoryLink.variableId));
      if (!comparedVariable) {
        return { valid: false, error: 'Compared variable not found' };
      }
      if (comparedVariable.type !== variable.type) {
        return { valid: false, error: `Slot value type is not valid in ${comparedVariable.name}` };
      }
    }
    return ok;
  }

  // Scalar value validation by variable type
  if (variable.type === MemoryType.Number) {
    const isNumbersOnly = /^-?\d+$/.test(String(value));
    if (!isNumbersOnly) {
      return { valid: false, error: 'Value should be a number' };
    }
  }

  if (variable.type === MemoryType.Boolean) {
    const strValue = String(value).toLowerCase();
    if (strValue !== 'true' && strValue !== 'false') {
      return { valid: false, error: 'Value should be true or false for boolean memory' };
    }
  }

  return ok;
}

/**
 * Sanitizes a check step value loaded from the API.
 * If the value is incompatible with the variable type, resets it to empty string.
 * Returns true if the value was sanitized (caller should show a warning).
 */
export function sanitizeCheckStepValue(
  check: StepCheck,
  memoryBank: MemoryDTO[],
): boolean {
  if (!check || !check.operator || !check.variableId) {
    return false;
  }

  if (OPERATORS_WITHOUT_SCALAR_VALUE.has(check.operator)) {
    return false;
  }

  const variable = memoryBank.find((slot) => slot.id === Number(check.variableId));
  if (!variable) {
    return false;
  }

  // Skip array values and undefined
  if (Array.isArray(check.value) || check.value === undefined) {
    return false;
  }

  // Skip variable-to-variable comparisons
  const memoryLink = getMemorySlotVariableLink(check.value as MemoryValue | MemoryLinkValue);
  if (memoryLink) {
    return false;
  }

  const result = validateCheckStepValue(check, memoryBank);
  if (!result.valid) {
    // eslint-disable-next-line no-param-reassign -- intentional in-place sanitization of loaded data
    check.value = '';
    return true;
  }

  return false;
}
